<template>
  <div class="calendar-view">
    <!-- Shared deliverable header, print only: on screen the cal-bar below
         (and the app banner above) already name the page. -->
    <PrintHeader
      print-only
      :title="printTitle"
      :partyAName="partyAName"
      :partyBName="partyBName"
      subtitle="Weekly rotation with holiday decisions applied"
    />
    <!-- ============ YEAR MODE ============ -->
    <template v-if="mode === 'year'">
      <div class="cal-bar">
        <button type="button" class="cal-nav" @click="year--" aria-label="Previous year">‹</button>
        <h3 class="cal-title">{{ year }}</h3>
        <button type="button" class="cal-nav" @click="year++" aria-label="Next year">›</button>
        <span class="cal-hint">Click a month to zoom in</span>
        <div class="cal-legend">
          <span class="swatch swatch-a"></span> {{ partyAName || 'Party A' }}
          <span class="swatch swatch-b"></span> {{ partyBName || 'Party B' }}
          <span class="swatch swatch-holiday"></span> holiday
          <span class="swatch swatch-shared"></span> shared/split
        </div>
      </div>

      <p class="cal-totals">
        In {{ year }}: <strong>{{ partyAName || 'Party A' }}</strong> {{ totals.aPct }}% ·
        <strong>{{ partyBName || 'Party B' }}</strong> {{ totals.bPct }}%<template v-if="totals.sharedPct !== '0'"> ·
        shared {{ totals.sharedPct }}%</template><template v-if="totals.nonePct !== '0'"> ·
        <span class="unassigned-note">unassigned {{ totals.nonePct }}%</span></template>
        <span class="totals-note">(holiday decisions override the rotation)</span>
      </p>
      <p v-if="anchorLabel" class="cal-anchor-note">Week 1 of the rotation begins {{ anchorLabel }}.</p>

      <div class="months-grid">
        <div v-for="(m, mi) in yearData" :key="m.label" class="month">
          <h4 class="month-name"><button type="button" class="month-link" @click="zoomMonth(mi)">{{ m.label }}</button></h4>
          <div class="month-grid">
            <span v-for="(d, i) in DOW" :key="`h${i}`" class="dow">{{ d }}</span>
            <span v-for="b in m.leadingBlanks" :key="`b${b}`" class="blank"></span>
            <span
              v-for="day in m.days"
              :key="day.d"
              class="day"
              :class="{ 'day-holiday': day.holiday }"
              :style="day.style"
              :title="day.title"
              @click="zoomDay(mi, day.d)"
            >{{ day.d }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- ============ MONTH MODE ============ -->
    <template v-else>
      <div class="cal-bar">
        <button type="button" class="cal-nav" @click="stepMonth(-1)" aria-label="Previous month">‹</button>
        <h3 class="cal-title cal-title-month">{{ MONTHS[month] }} {{ year }}</h3>
        <button type="button" class="cal-nav" @click="stepMonth(1)" aria-label="Next month">›</button>
        <button type="button" class="cal-back" @click="mode = 'year'; selectedDay = null">⊞ Year view</button>
        <div class="cal-legend">
          <span class="swatch swatch-a"></span> {{ partyAName || 'Party A' }}
          <span class="swatch swatch-b"></span> {{ partyBName || 'Party B' }}
          <span class="swatch swatch-holiday"></span> holiday
        </div>
      </div>

      <div class="big-month-grid">
        <span v-for="(d, i) in DOW_LONG" :key="`bh${i}`" class="big-dow">{{ d }}</span>
        <span v-for="b in monthData.leadingBlanks" :key="`bb${b}`" class="big-blank"></span>
        <button
          v-for="day in monthData.days"
          :key="day.d"
          type="button"
          class="big-day"
          :class="{ 'big-day-holiday': day.holiday, 'big-day-selected': selectedDay === day.d }"
          :title="day.title"
          @click="selectedDay = day.d"
        >
          <span class="big-day-head">
            <span class="big-day-num">{{ day.d }}</span>
            <span v-if="day.holiday" class="big-day-holiday-name">{{ day.holiday }}</span>
          </span>
          <span class="big-day-bar">
            <span
              v-for="(seg, si) in day.segs"
              :key="si"
              class="big-day-seg"
              :class="`seg-${seg.kind}`"
              :style="{ width: seg.pct + '%' }"
            ></span>
          </span>
        </button>
      </div>

      <div v-if="selectedDetail" class="day-detail">
        <h4 class="day-detail-title">{{ selectedDetail.heading }}</h4>
        <ul class="day-detail-runs">
          <li v-for="(run, ri) in selectedDetail.runs" :key="ri">
            <span class="run-dot" :class="`seg-${run.kind}`"></span>
            <strong>{{ run.who }}</strong>: {{ run.range }}
          </li>
        </ul>
        <p class="day-detail-source">{{ selectedDetail.source }}</p>
      </div>
      <p v-else class="day-detail-hint">Click a day for the hour-by-hour breakdown.</p>
    </template>
  </div>
</template>

<script>
import PrintHeader from './PrintHeader.vue'
import { resolveDay } from '@/lib/parentingSchedule/calendarPaint.js'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const DOW_LONG = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const COLOR_A = '#7797a1'
const COLOR_B = '#34555f'
const COLOR_NONE = '#eef1f4'
// Written with one position per stop on purpose. cssnano 4 (Vue CLI's
// production minifier) rewrites the two-position form `A 0 3px, B 3px 6px`
// to `A 3px, B 3px 6px`, which moves the first stop to 3px; a repeating
// gradient then repeats every 3px with a zero-width A stripe and paints
// solid B. The legend swatch below had exactly that on Divii's build.
const SHARED_STRIPES = `repeating-linear-gradient(135deg, ${COLOR_A} 0, ${COLOR_A} 3px, ${COLOR_B} 3px, ${COLOR_B} 6px)`

function segKind(partner) {
  if (partner === 'A') return 'a'
  if (partner === 'B') return 'b'
  return partner ? 'shared' : 'none'
}

// Hours can be fractional on a day split mid-hour; keep titles readable.
function fmtHours(h) {
  return Math.round(h * 10) / 10
}

function fmtMin(mins) {
  if (mins >= 1440) return 'end of day'
  const h24 = Math.floor(mins / 60)
  const mm = mins % 60
  const ampm = h24 < 12 ? 'am' : 'pm'
  const h12 = h24 === 0 ? 12 : (h24 > 12 ? h24 - 12 : h24)
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`
}

export default {
  name: 'CalendarView',
  components: { PrintHeader },
  props: {
    rotation: { type: Object, default: null },
    holidays: { type: Array, default: () => [] },
    jurisdiction: { type: String, default: 'BC' },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
  },
  data() {
    const anchorYear = Number(String(this.rotation?.anchorDate || '').slice(0, 4))
    return {
      MONTHS, DOW, DOW_LONG,
      mode: 'year', // 'year' | 'month'
      year: anchorYear || new Date().getFullYear(),
      month: 0,
      selectedDay: null,
    }
  },
  computed: {
    printTitle() {
      return this.mode === 'year'
        ? `${this.year} Year Calendar`
        : `${MONTHS[this.month]} ${this.year} Calendar`
    },
    anchorLabel() {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(this.rotation?.anchorDate || ''))
      if (!m) return ''
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      return d.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },
    yearData() {
      const year = this.year
      const months = []
      this._tally = { a: 0, b: 0, shared: 0, none: 0 } // hours
      for (let m = 0; m < 12; m++) {
        const daysInMonth = new Date(year, m + 1, 0).getDate()
        const days = []
        for (let d = 1; d <= daysInMonth; d++) days.push(this._smallDayCell(year, m, d))
        months.push({ label: MONTHS[m], leadingBlanks: new Date(year, m, 1).getDay(), days })
      }
      return months
    },
    totals() {
      this.yearData // recompute tally
      const t = this._tally
      const total = t.a + t.b + t.shared + t.none
      const pct = (v) => total ? String(Math.round((v / total) * 1000) / 10) : '0'
      return {
        aPct: pct(t.a + t.shared / 2),
        bPct: pct(t.b + t.shared / 2),
        sharedPct: pct(t.shared),
        nonePct: pct(t.none),
      }
    },
    monthData() {
      const year = this.year
      const m = this.month
      const daysInMonth = new Date(year, m + 1, 0).getDate()
      const days = []
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, m, d)
        // 30-minute resolution so a noon handover inside a paired Eve/Day
        // block shows as a real split rather than one solid bar.
        const info = resolveDay(date, this.rotation, this.holidays, { jurisdiction: this.jurisdiction, step: 30 })
        const segs = info.runs.map((r) => ({
          kind: segKind(r.partner),
          pct: ((r.endMin - r.startMin) / 1440) * 100,
        }))
        days.push({ d, holiday: info.holidayName, segs, title: this._dayTitle(date, info) })
      }
      return { leadingBlanks: new Date(year, m, 1).getDay(), days }
    },
    selectedDetail() {
      if (!this.selectedDay) return null
      const date = new Date(this.year, this.month, this.selectedDay)
      const info = resolveDay(date, this.rotation, this.holidays, { jurisdiction: this.jurisdiction })
      const aName = this.partyAName || 'Party A'
      const bName = this.partyBName || 'Party B'
      const heading = `${WEEKDAYS[date.getDay()]}, ${MONTHS[this.month]} ${this.selectedDay}, ${this.year}`
        + (info.holidayName ? ` — ${info.holidayName}` : '')
      const whoName = (p) => (p === 'A' ? aName : p === 'B' ? bName : p ? `${aName} and ${bName} (${p})` : 'Unassigned')
      const whole = info.runs.length === 1
      const runs = info.runs.map((r) => ({
        kind: segKind(r.partner),
        who: whoName(r.partner),
        range: whole ? 'all day' : `${fmtMin(r.startMin)} to ${fmtMin(r.endMin)}`,
      }))

      // Name every decision that had a say, so a day split between two of
      // them (Christmas Eve until noon, Christmas Day after) explains itself.
      const sources = []
      for (const r of info.runs) {
        if (r.fromHoliday && r.source && sources.indexOf(r.source) === -1) sources.push(r.source)
      }
      const followsRotation = info.runs.some((r) => !r.fromHoliday && r.partner)
      let source
      if (!sources.length) {
        source = info.holidayName
          ? `${info.holidayName} has no deciding rule yet, so this day follows the weekly rotation.`
          : 'Per the weekly parenting rotation.'
      } else {
        const list = sources.length === 1 ? sources[0]
          : sources.length === 2 ? `${sources[0]} and ${sources[1]}`
          : `${sources.slice(0, -1).join(', ')}, and ${sources[sources.length - 1]}`
        source = `Per the ${list} decision${sources.length > 1 ? 's' : ''} in the Holiday Schedule`
          + (followsRotation ? ', with the rest of the day following the weekly rotation.' : '.')
      }
      return { heading, runs, source }
    },
  },
  methods: {
    zoomMonth(mi) {
      this.month = mi
      this.selectedDay = null
      this.mode = 'month'
    },
    zoomDay(mi, d) {
      this.month = mi
      this.selectedDay = d
      this.mode = 'month'
    },
    stepMonth(dir) {
      let m = this.month + dir
      if (m < 0) { m = 11; this.year-- }
      if (m > 11) { m = 0; this.year++ }
      this.month = m
      this.selectedDay = null
    },
    _dayTitle(date, info) {
      const aName = this.partyAName || 'Party A'
      const bName = this.partyBName || 'Party B'
      const label = `${MONTHS[date.getMonth()].slice(0, 3)} ${date.getDate()}`
      if (info.holidayWho === 'A' || info.holidayWho === 'B') {
        return `${label} — ${info.holidayName}: ${info.holidayWho === 'A' ? aName : bName} (holiday decision)`
      }
      if (info.holidayWho) return `${label} — ${info.holidayName}: ${info.holidayWho} (holiday decision)`
      // A split day is still holiday-decided for the hours a decision covers;
      // only say "follows rotation" when no decision touched the day at all.
      const decided = (info.runs || []).some((r) => r.fromHoliday)
      const holidaySuffix = info.holidayName
        ? ` · ${info.holidayName}${decided ? ' (part day)' : ' (follows rotation)'}`
        : ''
      if (info.aHours === 0 && info.bHours === 0) return `${label} — unassigned${holidaySuffix}`
      if (info.bHours === 0) return `${label} — ${aName}${holidaySuffix}`
      if (info.aHours === 0) return `${label} — ${bName}${holidaySuffix}`
      return `${label} — ${aName} ${fmtHours(info.aHours)}h / ${bName} ${fmtHours(info.bHours)}h${holidaySuffix}`
    },
    _smallDayCell(year, m, d) {
      const date = new Date(year, m, d)
      const info = resolveDay(date, this.rotation, this.holidays, { jurisdiction: this.jurisdiction })
      const title = this._dayTitle(date, info)
      if (info.holidayWho === 'A' || info.holidayWho === 'B') {
        this._tally[info.holidayWho === 'A' ? 'a' : 'b'] += 24
        return { d, holiday: info.holidayName, style: { background: info.holidayWho === 'A' ? COLOR_A : COLOR_B, color: '#fff' }, title }
      }
      if (info.holidayWho) {
        this._tally.shared += 24
        return { d, holiday: info.holidayName, style: { background: SHARED_STRIPES, color: '#fff' }, title }
      }
      const { aHours: a, bHours: b } = info
      this._tally.a += a
      this._tally.b += b
      this._tally.none += 24 - a - b
      if (a === 0 && b === 0) return { d, holiday: info.holidayName, style: { background: COLOR_NONE, color: '#94a3b8' }, title }
      if (b === 0) return { d, holiday: info.holidayName, style: { background: COLOR_A, color: '#fff' }, title }
      if (a === 0) return { d, holiday: info.holidayName, style: { background: COLOR_B, color: '#fff' }, title }
      const frac = Math.round((a / (a + b)) * 100)
      const first = info.runs.find((r) => r.partner)
      const [c1, c2, f] = first?.partner === 'B' ? [COLOR_B, COLOR_A, 100 - frac] : [COLOR_A, COLOR_B, frac]
      return { d, holiday: info.holidayName, style: { background: `linear-gradient(135deg, ${c1} ${f}%, ${c2} ${f}%)`, color: '#fff' }, title }
    },
  },
}
</script>

<style scoped>
.calendar-view {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-family: inherit;
}
.cal-bar { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.cal-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: #34555f; min-width: 3.2em; text-align: center; }
.cal-title-month { min-width: 9em; }
.cal-nav {
  background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; color: #34555f;
  font-size: 1rem; line-height: 1; padding: 0.25rem 0.6rem; cursor: pointer; font-family: inherit;
}
.cal-nav:hover { border-color: #3f6772; background: #eef3f4; }
.cal-back {
  background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; color: #557d89;
  font-size: 0.78rem; font-weight: 500; padding: 0.3rem 0.7rem; cursor: pointer; font-family: inherit;
}
.cal-back:hover { border-color: #3f6772; background: #eef3f4; color: #34555f; }
.cal-hint { font-size: 0.72rem; color: #94a3b8; }
.cal-legend { margin-left: auto; display: flex; align-items: center; gap: 0.4rem; font-size: 0.75rem; color: #475569; flex-wrap: wrap; }
.swatch { display: inline-block; width: 12px; height: 12px; border-radius: 3px; margin-left: 0.6rem; }
.swatch-a { background: #7797a1; }
.swatch-b { background: #34555f; }
.swatch-holiday { background: #fff; box-shadow: inset 0 0 0 2px #f7a91b; }
/* One position per stop: see SHARED_STRIPES in the script for why. */
.swatch-shared { background: repeating-linear-gradient(135deg, #7797a1 0, #7797a1 3px, #34555f 3px, #34555f 6px); }

.cal-totals { margin: 0; font-size: 0.85rem; color: #1e293b; }
.cal-anchor-note { margin: 0; font-size: 0.75rem; color: #94a3b8; }
.cal-totals strong { color: #34555f; }
.totals-note { color: #94a3b8; font-size: 0.75rem; margin-left: 0.4rem; }
.unassigned-note { color: #b45309; }

/* ---- year mode ---- */
.months-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(215px, 1fr)); gap: 0.9rem; }
.month { min-width: 0; }
.month-name { margin: 0 0 0.3rem; }
.month-link {
  background: none; border: none; padding: 0; cursor: pointer; font-family: inherit;
  font-size: 0.82rem; font-weight: 600; color: #34555f;
}
.month-link:hover { color: #f7a91b; text-decoration: underline; }
.month-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.dow { font-size: 0.6rem; color: #94a3b8; text-align: center; font-weight: 600; }
.blank { aspect-ratio: 1; }
.day {
  aspect-ratio: 1; border-radius: 4px; font-size: 0.62rem; font-weight: 500;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  font-variant-numeric: tabular-nums;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.day-holiday { box-shadow: inset 0 0 0 2px #f7a91b; }

/* ---- month mode ---- */
.big-month-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
.big-dow { font-size: 0.68rem; color: #94a3b8; text-align: center; font-weight: 600; padding-bottom: 2px; }
.big-blank { min-height: 64px; }
.big-day {
  min-height: 64px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  padding: 4px 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  min-width: 0;
}
.big-day:hover { border-color: #3f6772; }
.big-day-selected { border-color: #34555f; box-shadow: 0 0 0 2px rgba(52, 85, 95, 0.25); }
.big-day-holiday { border-color: #f7a91b; box-shadow: inset 0 0 0 1px #f7a91b; }
.big-day-head { display: flex; align-items: baseline; gap: 4px; min-width: 0; }
.big-day-num { font-size: 0.78rem; font-weight: 600; color: #1e293b; font-variant-numeric: tabular-nums; }
.big-day-holiday-name {
  font-size: 0.58rem; color: #b45309; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.big-day-bar {
  display: flex; height: 10px; border-radius: 5px; overflow: hidden; background: #f1f5f9;
}
.big-day-seg { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.seg-a { background: #7797a1; }
.seg-b { background: #34555f; }
.seg-shared { background: repeating-linear-gradient(135deg, #7797a1 0 3px, #34555f 3px 6px); }
.seg-none { background: #f1f5f9; }

/* ---- day detail ---- */
.day-detail { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.7rem 0.9rem; background: #fafbfc; }
.day-detail-title { margin: 0 0 0.4rem; font-size: 0.9rem; font-weight: 600; color: #34555f; }
.day-detail-runs { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: #1e293b; }
.day-detail-runs strong { color: #34555f; }
.run-dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 0.45rem; }
.day-detail-source { margin: 0.45rem 0 0; font-size: 0.75rem; color: #94a3b8; }
.day-detail-hint { margin: 0; font-size: 0.78rem; color: #94a3b8; font-style: italic; }

@media print {
  /* One letter-portrait page for the whole year: PrintHeader carries the
     title (the on-screen cal-bar title hides), day cells flatten from
     squares to short chips, and the card chrome (border, padding) drops. */
  .calendar-view { padding: 0; border: none; border-radius: 0; gap: 0.35rem; }
  .cal-nav, .cal-back, .cal-hint, .cal-title, .day-detail-hint { display: none !important; }
  .cal-legend { margin-left: 0; font-size: 0.68rem; }
  .cal-totals { font-size: 0.75rem; }
  .cal-anchor-note { font-size: 0.65rem; }
  .months-grid { grid-template-columns: repeat(3, 1fr); gap: 0.4rem 0.7rem; }
  .month { break-inside: avoid; page-break-inside: avoid; }
  .month-name { margin-bottom: 0.15rem; }
  .month-link { font-size: 0.72rem; }
  .dow { font-size: 0.5rem; }
  .month-grid { gap: 1px 2px; }
  .day, .blank { aspect-ratio: auto; height: 16px; }
  .day { font-size: 0.58rem; border-radius: 2px; }
  /* Bootstrap 4's print stylesheet, compiled into hosts such as Divii, strips
     every box-shadow with `* { box-shadow: none !important }`. The holiday
     ring is a box-shadow, so re-assert it with a more specific !important
     rule for the day chips and the legend swatch. Without this the gold
     disappears from the printed calendar and its legend on those hosts. */
  .calendar-view .day-holiday { box-shadow: inset 0 0 0 1.5px #f7a91b !important; }
  .calendar-view .swatch-holiday { box-shadow: inset 0 0 0 2px #f7a91b !important; }
}
</style>
